import { Conversation } from 'src/services/whatsapp'
import { ConversationListItem } from './conversation-list-item'
import { Spinner } from '../spinner'
import { Input } from '../input'
import { Button } from '../button'
import { useState, useCallback, useEffect } from 'react'
import { MagnifyingGlassIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface ConversationListProps {
  conversations: Conversation[]
  selectedConversationId: string | null
  onSelectConversation: (conversation: Conversation) => void
  isLoading?: boolean
  isError?: boolean
  error?: Error | null
  searchTerm?: string
  onSearchChange?: (term: string) => void
  onNewConversation?: () => void
}

export const ConversationList = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  isLoading = false,
  isError = false,
  error = null,
  searchTerm = '',
  onSearchChange,
  onNewConversation,
}: ConversationListProps) => {
  const { t } = useTranslation()
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm)

  // Keep the raw load failure for debugging; the UI shows plain copy only.
  useEffect(() => {
    if (isError && error) console.error('Failed to load conversations', error)
  }, [isError, error])

  useEffect(() => {
    setLocalSearchTerm(searchTerm)
  }, [searchTerm])

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearchTerm(value)
      onSearchChange?.(value)
    },
    [onSearchChange],
  )

  return (
    <div className="flex h-full flex-col border-r border-border bg-surface">
      {/* Header with title and new conversation button */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-base font-semibold text-text-primary">{t('chat.conversations')}</h2>
        {onNewConversation && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onNewConversation}
            className="gap-1.5 text-sm"
            aria-label={t('chat.newConversation')}
          >
            <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
            {t('chat.new')}
          </Button>
        )}
      </div>

      {/* Search bar */}
      <div className="border-b border-border p-4">
        <Input
          type="text"
          placeholder={t('chat.searchPlaceholder')}
          value={localSearchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          icon={<MagnifyingGlassIcon className="h-5 w-5 text-text-tertiary" />}
          iconPosition="left"
          className="w-full"
        />
      </div>

      {/* Conversations list */}
      <div className="flex-1 overflow-y-auto">
        {isError ? (
          <div className="flex flex-col items-center justify-center gap-2 p-8 text-center">
            <ExclamationTriangleIcon className="h-10 w-10 text-error" aria-hidden />
            <p className="font-medium text-text-primary">{t('chat.failedToLoadConversations')}</p>
            {/* Plain reassurance; the raw error is logged, never shown. */}
            <p className="text-sm text-text-placeholder">{t('chat.somethingWentWrong')}</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Spinner size="md" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <p className="text-text-placeholder">{t('chat.noConversations')}</p>
            <p className="mt-2 text-sm text-text-tertiary">{t('chat.noConversationsHint')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {conversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                isSelected={selectedConversationId === conversation.id}
                onClick={() => onSelectConversation(conversation)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
